import { useState, useCallback, useRef, type MouseEvent } from "react";
import { useParams, useNavigate } from "react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { ArrowLeft, Send, Upload, Loader2, X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProgramaById } from "@/hooks/programas/useProgramas";
import { uploadPresignedFile } from "@retro-ia/shared/upload";
import { axiosInstance } from "@retro-ia/shared/axios";
import { downloadPrivateObject } from "@retro-ia/shared/upload";

// ─── Types ────────────────────────────────────────────────────────────────────

interface RetroSessionPayload {
  session_id: string;
  base_feedback: string;
  evaluation_source?: string;
  template_source?: string;
  evaluation_chunks?: string[];
  retrieved_contexts?: string[];
  suggested_questions?: string[];
  [key: string]: unknown;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

// ─── SSE helpers ──────────────────────────────────────────────────────────────

/** Read the /retro-api/evaluate SSE stream and return the final payload. */
async function streamEvaluate(
  options: {
    file: string;
    file_clean: string;
    index: string;
    programa: number;
    actividad: number;
  },
  callbacks: {
    onMessage?: (msg: string) => void;
    onChunk?: (chunk: string) => void;
    onComplete?: (payload: RetroSessionPayload) => void;
    onError?: (err: Error) => void;
  },
  signal?: AbortSignal,
): Promise<void> {
  const baseURL = axiosInstance.defaults.baseURL ?? "";
  const url = `${baseURL}/retro-api/evaluate`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(options),
      signal,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const reader = response.body?.getReader();
    if (!reader) throw new Error("No se pudo leer el stream");

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);

        const dataStr = raw
          .split("\n")
          .filter((l) => l.startsWith("data:"))
          .map((l) => l.slice(5).trimStart())
          .join("\n")
          .trim();

        if (!dataStr) continue;

        try {
          const event = JSON.parse(dataStr);
          if (event.type === "chunk" && event.content) {
            callbacks.onChunk?.(event.content as string);
          } else if (event.type === "progress" && event.message) {
            callbacks.onMessage?.(event.message as string);
          } else if (event.type === "done" && event.payload) {
            callbacks.onComplete?.(event.payload as RetroSessionPayload);
          } else if (event.type === "error") {
            throw new Error(
              (event.message as string) ?? "Error en la evaluación",
            );
          }
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message !== "JSON parse")
            throw parseErr;
          // Ignore JSON parse noise
        }
      }
    }
  } catch (err) {
    callbacks.onError?.(
      err instanceof Error ? err : new Error("Error desconocido"),
    );
  }
}

/** Stream a chat turn against /retro-api/preview/session-query. */
async function streamPreviewChat(
  options: {
    session_payload: RetroSessionPayload;
    question: string;
    index: string;
    actividad: number;
  },
  callbacks: {
    onDelta?: (delta: string) => void;
    onDone?: () => void;
    onError?: (err: Error) => void;
  },
  signal?: AbortSignal,
): Promise<void> {
  const baseURL = axiosInstance.defaults.baseURL ?? "";
  const url = `${baseURL}/retro-api/preview/session-query`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify({
        session_payload: options.session_payload,
        question: options.question,
        llm_provider: "OpenAI",
        index: options.index,
        top_k: 5,
        actividad: options.actividad,
      }),
      signal,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    if (!response.body) throw new Error("No stream body");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let done = false;

    while (!done) {
      const { value, done: streamDone } = await reader.read();
      done = streamDone;
      buffer += value ? decoder.decode(value, { stream: true }) : "";

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);

        const dataStr = raw
          .split("\n")
          .filter((l) => l.startsWith("data:"))
          .map((l) => l.slice(5).trimStart())
          .join("\n")
          .trim();

        if (!dataStr || dataStr === "[DONE]") {
          if (dataStr === "[DONE]") done = true;
          continue;
        }

        try {
          const payload = JSON.parse(dataStr) as {
            delta?: string | { content?: string };
            error?: unknown;
          };

          if (payload.error) {
            throw new Error("Error en la respuesta del coach");
          }

          const text =
            typeof payload.delta === "string"
              ? payload.delta
              : payload.delta?.content;
          if (text) callbacks.onDelta?.(text);
        } catch (e) {
          if (e instanceof Error && e.message !== "JSON parse") {
            throw e;
          }
        }
      }
    }

    callbacks.onDone?.();
  } catch (err) {
    if ((err as Error)?.name === "AbortError") return;
    callbacks.onError?.(
      err instanceof Error ? err : new Error("Error en el chat"),
    );
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ActividadPreviewPage() {
  const { id, actividadId } = useParams<{ id: string; actividadId: string }>();
  const navigate = useNavigate();

  const programaId = Number(id);
  const actId = Number(actividadId);

  const { data: programa, isLoading } = useProgramaById(programaId);

  const actividad = programa?.actividadPrograma.find((a) => a.id === actId);

  // ── Upload + evaluation state ──
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [feedback, setFeedback] = useState<string>("");
  const [sessionPayload, setSessionPayload] =
    useState<RetroSessionPayload | null>(null);
  const evalAbortRef = useRef<AbortController | null>(null);

  // ── Chat state ──
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const chatAbortRef = useRef<AbortController | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const handleDescargarAdjunto = async (
    e: MouseEvent,
    adjunto: { llave?: string; nombre?: string; url?: string },
  ) => {
    e.preventDefault();
    if (adjunto.llave) {
      setDownloadingKey(adjunto.llave);
      try {
        const { url } = await downloadPrivateObject({ key: adjunto.llave });
        window.open(url, "_blank");
      } finally {
        setDownloadingKey(null);
      }
    } else if (adjunto.url) {
      window.open(adjunto.url, "_blank");
    }
  };

  // ── Drag & drop ──
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  // ── Evaluate ──
  const handleEvaluar = async () => {
    if (!selectedFile || !actividad || !programa) return;

    if (!actividad.archivoLimpio?.llave) {
      toast.error(
        "Esta actividad no tiene un archivo de referencia configurado.",
      );
      return;
    }

    // Reset previous results
    setFeedback("");
    setSessionPayload(null);
    setChatMessages([]);
    setIsEvaluating(true);
    setUploadStatus("Subiendo archivo...");

    if (evalAbortRef.current) evalAbortRef.current.abort();
    const controller = new AbortController();
    evalAbortRef.current = controller;

    try {
      const ext = selectedFile.name.split(".").pop() ?? "bin";
      const ruta = `preview/${actId}/${Date.now()}.${ext}`;

      const uploaded = await uploadPresignedFile({
        file: selectedFile,
        ruta,
        fileName: selectedFile.name,
        fileType: selectedFile.type || "application/octet-stream",
        isPublic: true,
      });

      setUploadStatus("Generando retroalimentación...");
      let accumulated = "";

      await streamEvaluate(
        {
          file: uploaded.llave,
          file_clean: actividad.archivoLimpio.llave,
          index: programa.cerebro ?? "",
          programa: programaId,
          actividad: actId,
        },
        {
          onMessage: (msg) => setUploadStatus(msg),
          onChunk: (chunk) => {
            accumulated += chunk;
            setFeedback(accumulated);
          },
          onComplete: (payload) => {
            setSessionPayload(payload);
          },
          onError: (err) => {
            toast.error(`Error en evaluación: ${err.message}`);
          },
        },
        controller.signal,
      );

      setUploadStatus("");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al procesar el archivo",
      );
    } finally {
      setIsEvaluating(false);
      evalAbortRef.current = null;
    }
  };

  // ── Chat send ──
  const handleSendMessage = async () => {
    const question = chatInput.trim();
    if (!question || !sessionPayload || !actividad || !programa) return;

    setChatInput("");
    setIsChatLoading(true);

    const userMsg: ChatMessage = { role: "user", content: question };
    setChatMessages((prev) => [...prev, userMsg]);

    // Placeholder for assistant response
    setChatMessages((prev) => [...prev, { role: "assistant", content: "" }]);
    const assistantIdx = chatMessages.length + 1;

    if (chatAbortRef.current) chatAbortRef.current.abort();
    const controller = new AbortController();
    chatAbortRef.current = controller;

    await streamPreviewChat(
      {
        session_payload: sessionPayload,
        question,
        index: programa.cerebro ?? "",
        actividad: actId,
      },
      {
        onDelta: (delta) => {
          setChatMessages((prev) => {
            const arr = [...prev];
            const current = arr[assistantIdx];
            if (!current) return prev;
            arr[assistantIdx] = {
              ...current,
              content: current.content + delta,
            };
            return arr;
          });
        },
        onDone: () => {
          setIsChatLoading(false);
          chatAbortRef.current = null;
          setTimeout(
            () => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }),
            50,
          );
        },
        onError: (err) => {
          toast.error(`Error en chat: ${err.message}`);
          setIsChatLoading(false);
          chatAbortRef.current = null;
        },
      },
      controller.signal,
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-[var(--background-muted)]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  if (!actividad) {
    return (
      <div className="p-8 text-center text-[var(--destructive)] bg-[var(--background-muted)]">
        No se encontró la actividad. Verifica que el ID sea correcto.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-screen bg-[var(--background-muted)] text-[var(--foreground)]">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-black/10 dark:border-white/10 bg-[var(--card-background)] px-6 py-3 flex items-center gap-3 shadow-sm">
        <Button
          variant="icon"
          size="icon"
          onClick={() => navigate(-1)}
          className="shrink-0 text-[var(--foreground)] hover:text-[var(--primary)]"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[var(--foreground)]/50 uppercase tracking-wide font-medium">
            Vista previa · {programa?.nombre}
          </p>
          <h1 className="text-lg font-semibold truncate text-[var(--foreground)]">
            {actividad.nombre}
          </h1>
        </div>
        <span className="text-xs bg-[var(--accent-yellow)]/20 text-[var(--foreground)] border border-[var(--accent-yellow)]/40 px-2 py-0.5 rounded-full font-medium">
          Sin historial
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden divide-x divide-black/10 dark:divide-white/10">
        {/* Left: upload + feedback */}
        <div className="flex-1 flex flex-col overflow-y-auto p-6 gap-6 min-w-0">
          {(actividad.archivoLimpio?.llave ||
            actividad.archivoLimpio?.url ||
            actividad.archivoWord?.llave ||
            actividad.archivoWord?.url) && (
            <div className="rounded-xl border border-black/10 dark:border-white/10 bg-[var(--card-background)] p-4 flex flex-col gap-2">
              <p className="text-xs font-medium text-[var(--foreground)]/60 uppercase tracking-wide">
                Material de la actividad
              </p>
              <div className="flex flex-wrap gap-2">
                {(actividad.archivoLimpio?.llave ||
                  actividad.archivoLimpio?.url) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    disabled={
                      downloadingKey === actividad.archivoLimpio?.llave ||
                      !(
                        actividad.archivoLimpio?.llave ||
                        actividad.archivoLimpio?.url
                      )
                    }
                    onClick={(ev) =>
                      actividad.archivoLimpio &&
                      handleDescargarAdjunto(ev, actividad.archivoLimpio)
                    }
                  >
                    <Download className="h-4 w-4" />
                    PDF: {actividad.archivoLimpio?.nombre ?? "referencia"}
                  </Button>
                )}
                {(actividad.archivoWord?.llave ||
                  actividad.archivoWord?.url) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    disabled={
                      downloadingKey === actividad.archivoWord?.llave ||
                      !(
                        actividad.archivoWord?.llave ||
                        actividad.archivoWord?.url
                      )
                    }
                    onClick={(ev) =>
                      actividad.archivoWord &&
                      handleDescargarAdjunto(ev, actividad.archivoWord)
                    }
                  >
                    <Download className="h-4 w-4" />
                    Word: {actividad.archivoWord?.nombre ?? "documento"}
                  </Button>
                )}
              </div>
            </div>
          )}
          {/* Dropzone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-colors cursor-pointer
              ${
                isDragging
                  ? "border-[var(--primary)] bg-[var(--primary)]/5"
                  : "border-black/20 dark:border-white/20 hover:border-[var(--primary)]/60 bg-[var(--card-background)]"
              }`}
            onClick={() =>
              !isEvaluating &&
              document.getElementById("preview-file-input")?.click()
            }
          >
            <input
              id="preview-file-input"
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              className="hidden"
              onChange={handleFileChange}
              disabled={isEvaluating}
            />
            <Upload className="mx-auto h-10 w-10 text-[var(--foreground)]/40 mb-3" />
            {selectedFile ? (
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm font-medium text-[var(--foreground)] truncate max-w-xs">
                  {selectedFile.name}
                </span>
                <button
                  type="button"
                  className="text-[var(--foreground)]/50 hover:text-[var(--destructive)] transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  disabled={isEvaluating}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-[var(--foreground)]">
                  Arrastra el archivo aquí o haz clic para seleccionar
                </p>
                <p className="text-xs text-[var(--foreground)]/50 mt-1">
                  PDF, Word o texto plano
                </p>
              </div>
            )}
          </div>

          <Button
            onClick={handleEvaluar}
            disabled={!selectedFile || isEvaluating}
            className="w-full"
            size="lg"
          >
            {isEvaluating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {uploadStatus || "Procesando..."}
              </>
            ) : (
              "Generar retroalimentación (prueba)"
            )}
          </Button>

          {/* Feedback */}
          {feedback && (
            <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-[var(--card-background)] p-6 shadow-lg text-[var(--foreground)] prose prose-sm max-w-none [&_h1]:text-[var(--foreground)] [&_h2]:text-[var(--foreground)] [&_h3]:text-[var(--foreground)] [&_h4]:text-[var(--foreground)] [&_strong]:text-[var(--foreground)] [&_p]:text-[var(--foreground)] [&_li]:text-[var(--foreground)] [&_a]:text-[var(--primary)] [&_blockquote]:border-l-[var(--primary)] [&_blockquote]:text-[var(--foreground)]/70 [&_hr]:border-black/10 dark:[&_hr]:border-white/10 [&_code]:bg-[var(--background-muted)] [&_code]:text-[var(--foreground)] [&_pre]:bg-[var(--background-muted)]">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {feedback}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Right: chat */}
        <div className="w-96 shrink-0 flex flex-col bg-[var(--card-background)]">
          {!sessionPayload ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center">
              <div>
                <p className="font-medium text-[var(--foreground)]">
                  Chat con el coach
                </p>
                <p className="text-sm mt-1 text-[var(--foreground)]/50">
                  Disponible después de generar la retroalimentación
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.length === 0 && (
                  <p className="text-sm text-[var(--foreground)]/50 text-center pt-4">
                    Escribe una pregunta para comenzar la conversación
                  </p>
                )}
                {chatMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm
                        ${
                          msg.role === "user"
                            ? "bg-[var(--primary)] text-white"
                            : "bg-[var(--background-muted)] text-[var(--foreground)] border border-black/5 dark:border-white/10"
                        }`}
                    >
                      {msg.role === "assistant" ? (
                        <div className="prose prose-sm max-w-none [&_*]:text-[var(--foreground)] [&_a]:text-[var(--primary)] [&_code]:bg-[var(--card-background)] [&_code]:text-[var(--foreground)] [&_pre]:bg-[var(--card-background)] [&_strong]:text-[var(--foreground)] [&_blockquote]:text-[var(--foreground)]/70">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.content || "▋"}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-black/10 dark:border-white/10 p-3 flex gap-2 bg-[var(--card-background)]">
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  placeholder="Escribe una pregunta…"
                  disabled={isChatLoading}
                  className="flex-1 resize-none rounded-xl border border-black/20 dark:border-white/20 bg-[var(--background-muted)] text-[var(--foreground)] placeholder:text-[var(--foreground)]/40 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)] disabled:opacity-50 transition-colors"
                />
                <Button
                  size="icon"
                  onClick={handleSendMessage}
                  disabled={!chatInput.trim() || isChatLoading}
                  className="self-end shrink-0"
                >
                  {isChatLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
